import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ONE_TAP_GRACE_MS } from '../constants/auth';
import GoogleSignInButton from '../components/auth/GoogleSignInButton';
import { isDesktopChrome, isItpBrowser } from '../utils/browser';

const LoginScreen: React.FC = () => {
  const { authError, isLoading, isOneTapActive } = useAuth();
  // Startwert an AuthContext koppeln: hat dessen Gnadenfrist (z.B. bei Direktaufruf
  // einer geschützten URL) schon vor dem Mount von LoginScreen gegriffen, soll hier
  // keine zweite Wartezeit von vorne beginnen.
  const [isWaitingForOneTap, setIsWaitingForOneTap] = useState(() => isLoading);
  // Safari/iOS/Firefox nutzen für One Tap den interaktiven itp_support-Flow (Welcome-Page ->
  // Popup), der länger dauern kann als die sonst übliche Gnadenfrist. AuthContext
  // bricht ihn deshalb dort nicht mehr zeitgesteuert ab (PR-Review-Finding P2) -
  // im Gegenzug zeigt der Login-Screen den manuellen Button hier sofort parallel an,
  // statt auf das Ende von One Tap zu warten, wie von Google für ITP-Browser
  // empfohlen (One Tap läuft im Hintergrund unbeeinflusst weiter).
  const [isItp] = useState(isItpBrowser);
  // Der Button wird sonst erst nach Ende von One Tap gezeigt. Er rendert sich über
  // dieselbe zentral initialisierte GIS-Instanz und erzeugt daher keinen zweiten
  // Google-Callback.
  const showManualLogin = isItp || (!isOneTapActive && (!!authError || !isWaitingForOneTap));
  // Aktueller Chrome-Desktop-Bug in Googles FedCM-Implementierung lässt die
  // automatische Anmeldung (One Tap) dort scheitern (Android-Chrome, Firefox und
  // Safari sind nicht betroffen) - Hinweis nur für genau diese Nutzergruppe, damit
  // der manuelle Klick nicht wie ein App-Fehler wirkt.
  const [showChromeHint] = useState(isDesktopChrome);

  useEffect(() => {
    if (!isLoading) {
      setIsWaitingForOneTap(false);
      return;
    }
    const timeout = window.setTimeout(() => setIsWaitingForOneTap(false), ONE_TAP_GRACE_MS);
    return () => window.clearTimeout(timeout);
  }, [isLoading]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900 p-4">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            360Volt Dokumentation
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            Anmelden um fortzufahren
          </p>
        </div>

        {/* Falsches Konto */}
        {authError === 'wrong-account' && (
          <div className="bg-red-50 dark:bg-red-900 text-red-800 dark:text-red-200 p-3 rounded text-sm">
            Dieses Google-Konto ist nicht erlaubt. Bitte melde dich mit deiner{' '}
            <strong>@360volt.de</strong> E-Mail-Adresse an.
          </div>
        )}

        {/* Bekannter Chrome-Desktop-Bug: automatische Anmeldung schlägt fehl, manueller Klick funktioniert */}
        {showChromeHint && (
          <div className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 p-3 rounded text-sm">
            In Chrome (Desktop) funktioniert die automatische Anmeldung aktuell aufgrund eines
            bekannten Chrome-Fehlers nicht. Bitte melde dich einmalig über den Button unten an.
          </div>
        )}

        <div className="flex flex-col items-center gap-3">
          {showManualLogin ? (
            <GoogleSignInButton />
          ) : (
            <p className="text-sm text-gray-400 dark:text-gray-500">Anmeldung läuft…</p>
          )}

          {isLoading && (
            <p className="text-sm text-gray-400 dark:text-gray-500">Bitte warten...</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;
